import { useEffect, useMemo, useState } from 'react';
import { IFiltroLista } from '../../interfaces/IModuloBancario';

const TAMANHOS = [25, 50, 100];
const ESPERA_DIGITACAO_MS = 400;

/** Devolve o valor depois de a pessoa parar de digitar: cada filtro de texto é uma consulta ao servidor. */
export function useValorAtrasado<T>(valor: T): T {
    const [atrasado, setAtrasado] = useState(valor);
    useEffect(() => {
        const id = setTimeout(() => setAtrasado(valor), ESPERA_DIGITACAO_MS);
        return () => clearTimeout(id);
    }, [valor]);
    return atrasado;
}

/** Página e filtros de uma lista de documentos. Mudar um filtro volta à primeira página. */
export function useFiltroLista() {
    const [pagina, setPagina] = useState(1);
    const [tamanhoPagina, setTamanhoPagina] = useState(50);
    const [texto, setTextoBruto] = useState('');
    const [situacao, setSituacaoBruta] = useState('');
    const textoAtrasado = useValorAtrasado(texto);

    useEffect(() => { setPagina(1); }, [textoAtrasado]);

    const params = useMemo<IFiltroLista>(() => ({
        pagina,
        tamanhoPagina,
        texto: textoAtrasado || undefined,
        situacao: situacao || undefined,
    }), [pagina, tamanhoPagina, textoAtrasado, situacao]);

    return {
        params,
        pagina,
        tamanhoPagina,
        texto,
        situacao,
        ativo: !!(texto || situacao),
        setPagina,
        setTexto: setTextoBruto,
        setSituacao: (valor: string) => { setSituacaoBruta(valor); setPagina(1); },
        setTamanho: (valor: number) => { setTamanhoPagina(valor); setPagina(1); },
        limpar: () => { setTextoBruto(''); setSituacaoBruta(''); setPagina(1); },
    };
}

interface FiltroProps {
    filtro: ReturnType<typeof useFiltroLista>;
    placeholder: string;
    situacoes: Record<string, string>;
    children?: React.ReactNode;
}

export function BarraFiltros({ filtro, placeholder, situacoes, children }: FiltroProps) {
    return (
        <div className='d-flex flex-wrap align-items-center gap-2 mb-3'>
            <div className='position-relative' style={{ flex: '1 1 240px', maxWidth: 360 }}>
                <i className='fas fa-search text-muted position-absolute top-50 translate-middle-y ms-3'></i>
                <input className='form-control form-control-sm ps-9' placeholder={placeholder} aria-label={placeholder}
                    value={filtro.texto} onChange={e => filtro.setTexto(e.target.value)} />
            </div>
            <select className='form-select form-select-sm w-auto' aria-label='Situação' value={filtro.situacao} onChange={e => filtro.setSituacao(e.target.value)}>
                <option value=''>Todas as situações</option>
                {Object.entries(situacoes).map(([valor, nome]) => <option key={valor} value={valor}>{nome}</option>)}
            </select>
            {children}
            {filtro.ativo && <button className='btn btn-sm btn-link p-0' onClick={filtro.limpar}>Limpar filtros</button>}
        </div>
    );
}

interface PaginacaoProps {
    total: number;
    pagina: number;
    tamanhoPagina: number;
    onPagina: (pagina: number) => void;
    onTamanho?: (tamanho: number) => void;
}

export function PaginacaoModulo({ total, pagina, tamanhoPagina, onPagina, onTamanho }: PaginacaoProps) {
    const paginas = Math.max(1, Math.ceil(total / tamanhoPagina));

    // Depois de descartar itens, a página atual pode deixar de existir.
    useEffect(() => {
        if (pagina > paginas) onPagina(paginas);
    }, [pagina, paginas, onPagina]);

    if (total === 0) return null;
    const inicio = (pagina - 1) * tamanhoPagina + 1;
    const fim = Math.min(pagina * tamanhoPagina, total);

    return (
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3'>
            <span className='text-muted fs-7'>
                {inicio.toLocaleString('pt-BR')}–{fim.toLocaleString('pt-BR')} de {total.toLocaleString('pt-BR')}
            </span>
            <div className='d-flex align-items-center gap-2'>
                {onTamanho && (
                    <select className='form-select form-select-sm w-auto' aria-label='Itens por página' value={tamanhoPagina} onChange={e => onTamanho(Number(e.target.value))}>
                        {TAMANHOS.map(t => <option key={t} value={t}>{t} por página</option>)}
                    </select>
                )}
                <button className='btn btn-sm btn-icon btn-light' title='Primeira página' disabled={pagina <= 1} onClick={() => onPagina(1)}><i className='fas fa-angle-double-left'></i></button>
                <button className='btn btn-sm btn-icon btn-light' title='Página anterior' disabled={pagina <= 1} onClick={() => onPagina(pagina - 1)}><i className='fas fa-angle-left'></i></button>
                <span className='fs-7 text-nowrap'>Página {pagina.toLocaleString('pt-BR')} de {paginas.toLocaleString('pt-BR')}</span>
                <button className='btn btn-sm btn-icon btn-light' title='Próxima página' disabled={pagina >= paginas} onClick={() => onPagina(pagina + 1)}><i className='fas fa-angle-right'></i></button>
                <button className='btn btn-sm btn-icon btn-light' title='Última página' disabled={pagina >= paginas} onClick={() => onPagina(paginas)}><i className='fas fa-angle-double-right'></i></button>
            </div>
        </div>
    );
}

/** Busca de um modal de vínculo: as opções vêm limitadas do servidor. */
export function BuscaOpcoes({ placeholder, mostradas, total, onBuscar }: { placeholder: string; mostradas: number; total: number; onBuscar: (texto: string) => void }) {
    const [texto, setTexto] = useState('');
    const atrasado = useValorAtrasado(texto);
    const [buscado, setBuscado] = useState('');

    useEffect(() => {
        if (atrasado === buscado) return;
        setBuscado(atrasado);
        onBuscar(atrasado);
    }, [atrasado, buscado, onBuscar]);

    return (
        <div className='mb-3'>
            <input className='form-control form-control-sm' placeholder={placeholder} aria-label={placeholder} value={texto} onChange={e => setTexto(e.target.value)} />
            {total > mostradas && (
                <div className='text-muted fs-8 mt-1'>Mostrando {mostradas} de {total.toLocaleString('pt-BR')}. Refine a busca para ver as demais.</div>
            )}
        </div>
    );
}
